import { FC, useState } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faTimes } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '../LanguageSwitcher';

const HeaderWrapper = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  width: 100%;
  padding: 20px 0;
  background-color: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Logo = styled.a`
  color: #fff;
  font-size: 1.2rem;
  font-family: var(--secondary-font-family);
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  .site-name {
    position: relative;
    
    &:before {
      content: '<';
      opacity: 0.5;
      margin-right: 2px;
    }
    
    &:after {
      content: '/>';
      opacity: 0.5;
      margin-left: 2px;
    }
  }
  
  .cursor {
    display: inline-block;
    width: 8px;
    height: 18px;
    background-color: var(--primary-color);
    animation: blink 1s infinite;
  }

  @keyframes blink {
    50% {
      opacity: 0;
    }
  }
`;

const Nav = styled.nav<{ $isOpen: boolean }>`
  ul {
    display: flex;
    gap: 2rem;
    list-style: none;
  }

  a {
    color: #fff;
    text-decoration: none;
    font-family: var(--secondary-font-family);
    font-size: 0.9rem;
    opacity: 0.7;
    transition: all 0.3s ease;
    
    &:hover {
      opacity: 1;
      color: var(--primary-color);
    }

    &:before {
      content: '>';
      margin-right: 5px;
      opacity: 0;
      transition: all 0.3s ease;
    }

    &:hover:before {
      opacity: 1;
    }
  }

  @media (max-width: 722px) {
    position: fixed;
    top: 0;
    right: ${props => props.$isOpen ? '0' : '-100%'};
    height: 100vh;
    width: 250px;
    background: rgba(0, 0, 0, 0.95);
    padding: 80px 20px;
    transition: all 0.3s ease;

    ul {
      flex-direction: column;
      align-items: flex-start;
    }
  }
`;

const MenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  color: #fff;
  font-size: 1.5rem;
  cursor: pointer;
  z-index: 100;

  @media (max-width: 722px) {
    display: block;
  }
`;

const Header: FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t } = useTranslation();

  return (
    <HeaderWrapper>
      <Container>
        <Logo href="/">
          <span className="site-name">{t('nav.logo')}</span>
          <span className="cursor"></span>
        </Logo>
        <MenuButton onClick={() => setIsMenuOpen(!isMenuOpen)}>
          <FontAwesomeIcon icon={isMenuOpen ? faTimes : faBars} />
        </MenuButton>
        <Nav $isOpen={isMenuOpen}>
          <ul>
            <li><a href="#home">{t('nav.home')}</a></li>
            <li><a href="#about">{t('nav.about')}</a></li>
            <li><a href="#projects">{t('nav.projects')}</a></li>
            <li><a href="#contact">{t('nav.contact')}</a></li>
            <li><LanguageSwitcher /></li>
          </ul>
        </Nav>
      </Container>
    </HeaderWrapper>
  );
};

export default Header;